import { useState, useRef, useEffect } from 'react';
import { gsap } from 'gsap';
import './FlightButton.scss';
import { EXT_STATE, INT_STATE } from './../states';

export default function FlightButton({ 
  initialState,
  state,
  onSetState,
  travelInfo = {
    origin: 'SFO',
    destination: "Levi's Stadium",
    city: 'San Francisco',
    travelCarTime: 6,
    travelJobyTime: 43
  },
  onClick,
}) {
  const [internalState, setInternalState] = useState(initialState);
  const setState = onSetState ?? setInternalState;

  const prevStateRef = useRef(null);
  const isInitializedRef = useRef(false);
  
  const takeoffButtonRef = useRef(null);
  const travelButtonRef = useRef(null);
  const landButtonRef = useRef(null);
  const partnershipButtonRef = useRef(null);
  
  const travelHeaderRef = useRef(null);
  const travelTitleRef = useRef(null);
  const travelDividerRef = useRef(null);
  const travelRouteRef = useRef(null);
  const travelTimeRef = useRef(null);
  const travelLegalRef = useRef(null);

  const buttons = {
    [EXT_STATE.LANDED]: takeoffButtonRef,
    [EXT_STATE.FLY_LEAVING]: travelButtonRef,
    [EXT_STATE.PARTNERSHIP]: partnershipButtonRef,
    [EXT_STATE.FLY_IDLE]: landButtonRef,
    [INT_STATE.LANDED]: takeoffButtonRef,
    [INT_STATE.TAKEOFF]: travelButtonRef,
  };

  const getButton = (state) => {
    return buttons[state]?.current || null;
  };

  const getTravelElements = () => [
    travelTitleRef.current,
    travelDividerRef.current,
    travelRouteRef.current,
    travelTimeRef.current,
    travelLegalRef.current
  ].filter(Boolean);

  const setupTravel = () => {
    if (!travelHeaderRef.current || !travelTitleRef.current) return;
    gsap.set(travelHeaderRef.current, { display: 'block', opacity: 1 });
    gsap.set(getTravelElements(), { opacity: 0, y: 10 });
    if (travelDividerRef.current) gsap.set(travelDividerRef.current, { '--divider-width': '0%' });
  };

  const animateTravelIn = (tl, pos) => {
    const elements = getTravelElements();
    if (!elements.length) return;
    const anim = { opacity: 1, y: 0, duration: 0.4, stagger: 0.1, ease: 'power2.out' };
    if (tl && pos !== null) tl.to(elements, anim, pos);
    else if (tl) tl.to(elements, anim);
    else gsap.to(elements, { ...anim, delay: 0.2 });
  };

  const animateDivider = (tl, pos, duration = 10) => {
    if (!travelDividerRef.current || !travelButtonRef.current) return;
    gsap.set(travelDividerRef.current, { '--divider-width': '0%' });
    
    const hideHeader = () => {
      if (!travelHeaderRef.current || !travelButtonRef.current) return;
      const h = travelButtonRef.current.offsetHeight;
      gsap.to(travelHeaderRef.current, {
        opacity: 0,
        duration: 0.4,
        ease: 'power2.in',
        onComplete: () => {
          gsap.set(travelHeaderRef.current, { display: 'none' });
          gsap.fromTo(travelButtonRef.current, 
            { height: h },
            { height: travelButtonRef.current.offsetHeight, duration: 0.4, ease: 'power2.inOut',
              onComplete: () => gsap.set(travelButtonRef.current, { height: 'auto' })
            }
          );
        }
      });
    };

    const anim = { '--divider-width': '100%', duration, ease: 'linear', onComplete: hideHeader };
    if (tl && pos !== null) tl.to(travelDividerRef.current, anim, pos);
    else if (tl) tl.to(travelDividerRef.current, anim);
    else gsap.to(travelDividerRef.current, { ...anim, delay: 0.6 });
  };

  const resetTravel = () => {
    if (travelHeaderRef.current) gsap.set(travelHeaderRef.current, { display: 'block', opacity: 1 });
    if (travelDividerRef.current) gsap.set(travelDividerRef.current, { '--divider-width': '0%' });
  };

  useEffect(() => {
    const prevState = prevStateRef.current;
    const isFirst = !isInitializedRef.current;
    
    if (isFirst) {
      gsap.set([takeoffButtonRef.current, travelButtonRef.current, landButtonRef.current, partnershipButtonRef.current]
        .filter(Boolean), { opacity: 0, display: 'none' });
      isInitializedRef.current = true;
      const btn = getButton(state);
      if (btn) {
        gsap.set(btn, { display: 'inline-flex', opacity: 1, y: 0, xPercent: -50 });
        if (state === EXT_STATE.FLY_LEAVING || state === INT_STATE.TAKEOFF) {
          setupTravel();
          requestAnimationFrame(() => { animateTravelIn(); animateDivider(); });
        }
      }
      prevStateRef.current = state;
      return;
    }
    
    if (prevState === state) return;

    const prevBtn = getButton(prevState);
    const nextBtn = getButton(state);

    if (!nextBtn) {
      if (prevBtn) {
        gsap.to(prevBtn, {
          opacity: 0, y: -20, xPercent: -50, duration: 0.4, ease: 'power2.in',
          onComplete: () => {
            gsap.set(prevBtn, { display: 'none' });
            if (prevState === EXT_STATE.FLY_LEAVING ) resetTravel();
          }
        });
      }
      prevStateRef.current = state;
      return;
    }

    if (!prevBtn) {
      if (state === EXT_STATE.FLY_LEAVING || state === INT_STATE.TAKEOFF) setupTravel();
      gsap.set(nextBtn, { display: 'inline-flex', opacity: 0, y: 20, xPercent: -50 });
      gsap.to(nextBtn, {
        opacity: 1, y: 0, xPercent: -50, duration: 0.4, ease: 'power2.out',
        onComplete: () => {
          if (state === EXT_STATE.FLY_LEAVING || state === INT_STATE.TAKEOFF) { animateTravelIn(); animateDivider(); }
        }
      });
      prevStateRef.current = state;
      return;
    }

    const tl = gsap.timeline();

    if (prevState === EXT_STATE.FLY_LEAVING) {
      const elements = getTravelElements();
      if (elements.length) tl.to(elements, { opacity: 0, y: -10, duration: 0.2, stagger: 0.05, ease: 'power2.in' });
    }

    if (state === EXT_STATE.FLY_LEAVING || state === INT_STATE.TAKEOFF) {
      gsap.set(nextBtn, { display: 'inline-flex', opacity: 0, y: 20, xPercent: -50 });
      setupTravel();
    }

    tl.to(prevBtn, {
      opacity: 0, y: -20, xPercent: -50, duration: 0.4, ease: 'power2.in',
      onComplete: () => {
        gsap.set(prevBtn, { display: 'none' });
        if (prevState === EXT_STATE.FLY_LEAVING) resetTravel();
      }
    }, prevState === EXT_STATE.FLY_LEAVING ? '-=0.1' : 0);

    if (state !== EXT_STATE.FLY_LEAVING || state !== INT_STATE.TAKEOFF) {
      tl.set(nextBtn, { display: 'inline-flex', opacity: 0, y: 20, xPercent: -50 });
    }

    tl.to(nextBtn, { opacity: 1, y: 0, xPercent: -50, duration: 0.4, ease: 'power2.out' });

    if (state === EXT_STATE.FLY_LEAVING || state === INT_STATE.TAKEOFF) {
      animateTravelIn(tl, '+=0.1');
      animateDivider(tl, '+=0.5', 10);
    }

    prevStateRef.current = state;
  }, [state]);

  const handleClick = () => {
    if (state === EXT_STATE.FLY_LEAVING) return;
    onClick?.();
  };

  const helicopterIcon = (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M24 8.68066C23.9998 9.15715 23.6642 9.56712 23.1982 9.66699C21.9721 9.92983 19.6046 10.4651 18 11C16.2337 11.5888 16.006 13.1012 15.5068 14.4502C15.5497 14.4898 15.5902 14.5333 15.624 14.584L16.3789 15.7168C16.5495 15.5813 16.7652 15.5 17 15.5C17.5523 15.5 18 15.9477 18 16.5V18C18 18.5523 17.5523 19 17 19C16.4477 19 16 18.5523 16 18V17.8516L14.6904 15.8877C13.638 16.9997 12 17 12 17C12 17 10.361 17.0002 9.30859 15.8877L8 17.8516V18C8 18.5523 7.55228 19 7 19C6.44772 19 6 18.5523 6 18V16.5C6 15.9477 6.44772 15.5 7 15.5C7.23455 15.5 7.44957 15.5816 7.62012 15.7168L8.37598 14.584C8.40964 14.5335 8.44953 14.4897 8.49219 14.4502C7.99311 13.1012 7.76615 11.5887 6 11C4.39537 10.4651 2.02791 9.92983 0.801758 9.66699C0.335817 9.56712 0.000219451 9.15715 0 8.68066V8H24V8.68066ZM9.22363 9.5C8.4559 9.5 7.97378 10.3295 8.35449 10.9961L10.4043 14.582C10.468 14.6934 10.546 14.796 10.6367 14.8867C11.3895 15.6395 12.6105 15.6395 13.3633 14.8867C13.454 14.796 13.532 14.6934 13.5957 14.582L15.6455 10.9961C16.0262 10.3295 15.5441 9.5 14.7764 9.5H9.22363ZM9 5.5C9 6.05228 8.55229 6.5 8 6.5H1C0.447715 6.5 1.61065e-08 6.05228 0 5.5V5H9V5.5ZM24 5.5C24 6.05228 23.5523 6.5 23 6.5H16C15.4477 6.5 15 6.05228 15 5.5V5H24V5.5Z" fill="#0E1620"/>
    </svg>
  );

  const arrowIcon = (
    <svg xmlns="http://www.w3.org/2000/svg" width="23" height="13" viewBox="0 0 23 13" fill="none">
      <path d="M8.84234 1.27274C11.2714 0.705977 13.8964 1.0527 16.2631 2.65064C18.6192 4.24147 20.6461 7.02091 22.0119 11.1943L20.3348 11.7434C19.054 7.82982 17.2206 5.42611 15.2758 4.11297C13.3418 2.80718 11.2252 2.52878 9.24325 2.99124C5.22386 3.92912 1.76447 7.91663 1.76445 12.6452H0C1.84614e-05 7.08113 4.03953 2.39341 8.84234 1.27274Z" fill="#F5F4DF"/>
      <path d="M22.9378 0V12.6452H11.763V10.8808H21.1734V0H22.9378Z" fill="#F5F4DF"/>
    </svg>
  );

  return (
    <>
      <button ref={takeoffButtonRef} className="flight-button flight-button--takeoff" onClick={handleClick}>
        {helicopterIcon}
        <span className="flight-button__text">Vertical Take Off</span>
      </button>

      <button ref={travelButtonRef} className="flight-button flight-button--travel">
        <div ref={travelHeaderRef} className="flight-button__travel-header">
          <span ref={travelTitleRef} className="flight-button__travel-title">Be there in minutes.</span>
          <div ref={travelDividerRef} className="flight-button__travel-divider"></div>
        </div>
        <div ref={travelRouteRef} className="flight-button__travel-route">
          <span className="flight-button__travel-origin">{travelInfo.origin}</span>
          <span className="flight-button__travel-arrow">{arrowIcon}</span>
          <div className="flight-button__travel-destination">
            <span className="flight-button__travel-dest-name">{travelInfo.destination}</span>
            <span className="flight-button__travel-dest-city">{travelInfo.city}</span>
          </div>
        </div>
        {/*<div ref={travelTimeRef} className="flight-button__travel-time">
          <span className="flight-button__travel-time-label-car">By car: <span>{travelInfo.travelCarTime}*</span></span>
          <span className="flight-button__travel-time-label-joby">By Joby: <span>{travelInfo.travelJobyTime}</span></span>
        </div>*/}
        <span ref={travelTimeRef} className="flight-button__travel-time-label-car">68 minutes faster by air*</span>
        <div ref={travelLegalRef} className="flight-button__legal">
          *Based on peak travel times.
        </div>
      </button>

      <button ref={landButtonRef} className="flight-button flight-button--land" onClick={handleClick}>
        {helicopterIcon}
        <span className="flight-button__text">Vertical land</span>
      </button>

      <div ref={partnershipButtonRef} className="flight-button flight-button--partnership">
        <div className="flight-button__partnership-content">
          <div className="flight-button__partnership-text">
            <span className="flight-button__partnership-line1">Reimagine your ride with</span>
            <span className="flight-button__partnership-line2">Joby and Uber.</span>
          </div>
        </div>
      </div>
    </>
  );
}
